import { useMemo } from 'react';
import { SignificantDate } from '@marageti/z4-sdk/lib/people';
import { useTravelerProfileSuggestionsStore } from '../store';
import {
  SuggestionEntry,
  SuggestionLocator,
  SuggestionPreview,
  SuggestionTextField,
  TravelPreferenceSubKey,
} from '../store/contexts/traveler-profile-suggestions/types';

export type { SuggestionPreview } from '../store/contexts/traveler-profile-suggestions/types';

type PreviewOptions = {
  /**
   * True if the field already has saved data on the profile. Used only to
   * gate the loading skeleton: when content already exists we keep showing
   * it during the MCP call rather than visually clobbering it with a
   * skeleton.
   */
  hasContent: boolean;
};

type PreviewFlags = {
  isSaving: boolean;
  isReviewing: boolean;
  isLoadingSuggestions: boolean;
};

type PreviewActions = {
  apply: () => void;
  dismiss: () => void;
  unapply: () => void;
  undismiss: () => void;
};

/**
 * Pure projection: receives the entry plus pre-bound action closures for
 * the locator the caller cares about, and returns the normalized
 * `SuggestionPreview` shape consumed by the UI.
 */
const buildPreview = <V>(
  entry: SuggestionEntry<V> | undefined,
  flags: PreviewFlags,
  hasContent: boolean,
  actions: PreviewActions,
): SuggestionPreview<V> => {
  if (entry?.status === 'pending') {
    return {
      state: 'pending',
      value: entry.value,
      apply: actions.apply,
      dismiss: actions.dismiss,
      disabled: flags.isSaving,
    };
  }
  if (entry?.status === 'accepted') {
    return { state: 'accepted', value: entry.value, undo: actions.unapply };
  }
  if (entry?.status === 'dismissed') {
    return { state: 'dismissed', undo: actions.undismiss };
  }
  if (flags.isReviewing && flags.isLoadingSuggestions && !hasContent) {
    return { state: 'loading' };
  }
  return { state: 'idle', lockedForReview: flags.isReviewing };
};

type ContextActionRefs = {
  apply: ((locator: SuggestionLocator) => void) | undefined;
  dismiss: ((locator: SuggestionLocator) => void) | undefined;
  unapply: ((locator: SuggestionLocator) => void) | undefined;
  undismiss: ((locator: SuggestionLocator) => void) | undefined;
};

const bindActions = (refs: ContextActionRefs, locator: SuggestionLocator): PreviewActions => ({
  apply: () => refs.apply?.(locator),
  dismiss: () => refs.dismiss?.(locator),
  unapply: () => refs.unapply?.(locator),
  undismiss: () => refs.undismiss?.(locator),
});

/**
 * Preview hook for a free-text profile field (loyalty programs, gifts
 * received, household details).
 *
 * The result is memoized on primitive deps + the stable provider
 * callbacks, so the returned object identity is preserved across renders
 * when nothing relevant changed. This keeps `React.memo`'d children that
 * receive `preview.apply` / `preview.dismiss` / `preview.undo` from
 * re-rendering unnecessarily.
 */
export const useTextSuggestionPreview = (
  field: SuggestionTextField,
  { hasContent }: PreviewOptions,
): SuggestionPreview<string> => {
  const ctx = useTravelerProfileSuggestionsStore();
  const entry = ctx?.suggestions.text[field];
  const apply = ctx?.apply;
  const dismiss = ctx?.dismiss;
  const unapply = ctx?.unapply;
  const undismiss = ctx?.undismiss;
  const isSaving = ctx?.isSaving ?? false;
  const isReviewing = ctx?.isReviewing ?? false;
  const isLoadingSuggestions = ctx?.isLoadingSuggestions ?? false;

  return useMemo(
    () => buildPreview(
      entry,
      { isSaving, isReviewing, isLoadingSuggestions },
      hasContent,
      bindActions({
        apply, dismiss, unapply, undismiss,
      }, { kind: 'text', field }),
    ),
    [entry, isSaving, isReviewing, isLoadingSuggestions, hasContent, field, apply, dismiss, unapply, undismiss],
  );
};

/**
 * Preview hook for a single travel-preference sub-category (e.g.
 * `flights`, `dining`, `accommodations`).
 */
export const useTravelPreferenceSuggestionPreview = (
  subKey: TravelPreferenceSubKey,
  { hasContent }: PreviewOptions,
): SuggestionPreview<string> => {
  const ctx = useTravelerProfileSuggestionsStore();
  const entry = ctx?.suggestions.travelPreferences[subKey];
  const apply = ctx?.apply;
  const dismiss = ctx?.dismiss;
  const unapply = ctx?.unapply;
  const undismiss = ctx?.undismiss;
  const isSaving = ctx?.isSaving ?? false;
  const isReviewing = ctx?.isReviewing ?? false;
  const isLoadingSuggestions = ctx?.isLoadingSuggestions ?? false;

  return useMemo(
    () => buildPreview(
      entry,
      { isSaving, isReviewing, isLoadingSuggestions },
      hasContent,
      bindActions({
        apply, dismiss, unapply, undismiss,
      }, { kind: 'travelPreference', subKey }),
    ),
    [entry, isSaving, isReviewing, isLoadingSuggestions, hasContent, subKey, apply, dismiss, unapply, undismiss],
  );
};

/**
 * Preview hook for the significant-dates list. There's only ever one
 * entry for this field, so no key argument is needed.
 */
export const useSignificantDatesSuggestionPreview = (
  { hasContent }: PreviewOptions,
): SuggestionPreview<SignificantDate[]> => {
  const ctx = useTravelerProfileSuggestionsStore();
  const entry = ctx?.suggestions.significantDates;
  const apply = ctx?.apply;
  const dismiss = ctx?.dismiss;
  const unapply = ctx?.unapply;
  const undismiss = ctx?.undismiss;
  const isSaving = ctx?.isSaving ?? false;
  const isReviewing = ctx?.isReviewing ?? false;
  const isLoadingSuggestions = ctx?.isLoadingSuggestions ?? false;

  return useMemo(
    () => buildPreview(
      entry,
      { isSaving, isReviewing, isLoadingSuggestions },
      hasContent,
      bindActions({
        apply, dismiss, unapply, undismiss,
      }, { kind: 'significantDates' }),
    ),
    [entry, isSaving, isReviewing, isLoadingSuggestions, hasContent, apply, dismiss, unapply, undismiss],
  );
};
